import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Accordion, ButtonUI } from 'sonata-react-components';

import './faqSection.styles.scss';

type FaqItem = {
  question: string;
  answer: string;
};

type Props = {
  /** Callback to close/dismiss the FAQ section and return to the previous content. */
  onClose: () => void;
};

const FAQ_ITEM_COUNT = 21;

/**
 * FAQ section component that renders a searchable list of frequently asked questions
 * using the Accordion component. Intended to be embedded in page layouts as a
 * full-content replacement activated via the PageFooter FAQs link.
 */
export function FaqSection({ onClose }: Props) {
  const { formatMessage } = useIntl();
  const [searchTerm, setSearchTerm] = useState('');

  const items: FaqItem[] = Array.from({ length: FAQ_ITEM_COUNT }, (_, index) => {
    const num = index + 1;
    return {
      answer: formatMessage({ id: `faq.description${num}` }),
      question: formatMessage({ id: `faq.text${num}` })
    };
  });

  const normalizedSearch = searchTerm.toLowerCase();
  const filteredItems = normalizedSearch
    ? items.filter(
        item =>
          item.question.toLowerCase().includes(normalizedSearch) || item.answer.toLowerCase().includes(normalizedSearch)
      )
    : items;

  return (
    <div className="faqSection">
      <div className="faqSection__header">
        <ButtonUI
          className="faqSection__backBtn"
          icon="fal fa-arrow-left"
          iconPosition="left"
          id="faqHeader_backButton"
          onClick={onClose}
          shape="squareRounded"
          size="small"
          type="secondary"
        />
        <h1 className="faqSection__title">
          {formatMessage({ defaultMessage: 'Frequently Asked Questions', id: 'faq.title' })}
        </h1>
      </div>

      <div className="faqSection__body">
        <div className="faqSection__searchWrapper">
          <input
            className="faqSection__search"
            onChange={e => setSearchTerm(e.target.value)}
            placeholder={formatMessage({ defaultMessage: 'Search by key word...', id: 'faq.searchPlaceholder' })}
            type="text"
            value={searchTerm}
          />
          <i className="faqSection__searchIcon fal fa-search" />
        </div>

        <div className="faqSection__list">
          {filteredItems.map((item, index) => (
            <div className="faqSection__accordionGroup" key={index}>
              <Accordion>
                <Accordion.Header>
                  {item.question}
                  <Accordion.Icon />
                </Accordion.Header>
                <Accordion.Content>{item.answer}</Accordion.Content>
              </Accordion>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
